// @ts-nocheck
// Vendored from roblouie/gameboy-emulator with MultiNostalgia patches
// (see ../../PATCHES.md):
//   - run() / runFrame(rAF-driven loop) replaced with runCycles(n) so
//     the host drives timing deterministically and the file has no
//     browser-globals (requestAnimationFrame, performance) at all.
//     The cycle math is preserved.
//   - frameFinishedCallback still fires from runCycles when the host
//     opts to invoke it; adapters that read the framebuffer directly
//     after runCycles can ignore it.
import { GPU } from "./gpu/gpu.js";
import { CPU } from "./cpu/cpu.js";
import { Memory } from "./memory/memory.js";
import { Input } from "./input/input.js";
import { APU } from "./apu/apu.js";
import { ControllerManager } from "./input/controller-manager.js";
import { CartridgeType } from "./cartridge/cartridge-type.enum.js";
import { CartridgeLoader } from "./cartridge/cartridge-loader.js";
import { KeyboardManager } from "./input/keyboard-manager.js";
import { InterruptController } from "./cpu/interrupt-request-register.js";
import { TimerController } from "./cpu/timer-controller.js";
export class Gameboy {
    interruptController = new InterruptController();
    timerController = new TimerController(this.interruptController);
    gpu = new GPU(this.interruptController);
    apu = new APU();
    // MultiNostalgia patch: input is now per-instance (was a shared module
    // singleton — see input.ts). Declared before `bus` so the field
    // initializer order threads this Input into Memory, which reads/writes
    // it at $FF00. Two Gameboys in one process now have independent
    // controllers and independent loadState.
    input = new Input();
    bus = new Memory(this.gpu, this.apu, this.input, this.interruptController, this.timerController);
    cpu = new CPU(this.bus, this.interruptController, this.timerController);
    frameFinishedCallback;
    controllerManager = new ControllerManager(this.input);
    keyboardManager = new KeyboardManager(this.input);
    // Boot the CPU + zero memory. Replaces upstream's run(), which also
    // kicked off a requestAnimationFrame loop — the host now drives
    // frames via runCycles().
    initialize() {
        this.cpu.initialize();
        this.bus.reset();
    }
    // Tick the system for `cycles` CPU cycles. The host typically calls
    // this with one frame's worth of cycles (CPU.OperatingHertz / 59.7275
    // ≈ 70224 — see GPU.HeightIncludingOffscreen × cycles-per-scanline).
    // Returns the number of cycles actually run (may slightly exceed
    // `cycles` because each cpu.tick() advances by an instruction-sized
    // step, not a single cycle).
    runCycles(cycles) {
        let ran = 0;
        while (ran < cycles) {
            const c = this.cpu.tick();
            this.gpu.tick(c);
            this.apu.tick(c);
            ran += c;
        }
        if (this.frameFinishedCallback) {
            this.frameFinishedCallback(this.gpu.displayImageData);
        }
        return ran;
    }
    onFrameFinished(callback) {
        this.frameFinishedCallback = callback;
    }
    loadGame(arrayBuffer) {
        const cartridge = CartridgeLoader.FromArrayBuffer(arrayBuffer);
        this.bus.insertCartridge(cartridge);
    }
    // PATCH: fixed operator-precedence bug — `x === A || B` parsed as
    // `(x === A) || B` with B always truthy, so the guard fired for EVERY
    // cartridge (incl. ROM-only) and cast it to a save-RAM cart. Both
    // Mbc1Cartridge and Mbc3Cartridge implement setRam/dumpRam/onSramWrite,
    // so the cast is correct once the type is actually one of them.
    setCartridgeSaveRam(sramArrayBuffer) {
        if (this.bus.cartridge?.type === CartridgeType.MBC1_RAM_BATTERY || this.bus.cartridge?.type === CartridgeType.MBC3_RAM_BATTERY) {
            const cartridge = this.bus.cartridge;
            cartridge.setRam(sramArrayBuffer);
        }
    }
    getCartridgeSaveRam() {
        if (this.bus.cartridge?.type === CartridgeType.MBC1_RAM_BATTERY || this.bus.cartridge?.type === CartridgeType.MBC3_RAM_BATTERY) {
            const cartridge = this.bus.cartridge;
            return cartridge.dumpRam();
        }
    }
    setOnWriteToCartridgeRam(onSramWrite) {
        if (this.bus.cartridge?.type === CartridgeType.MBC1_RAM_BATTERY || this.bus.cartridge?.type === CartridgeType.MBC3_RAM_BATTERY) {
            const cartridge = this.bus.cartridge;
            cartridge.onSramWrite = onSramWrite;
        }
    }
}
//# sourceMappingURL=gameboy.js.map